import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Play, Pause, RotateCcw, ArrowUpRight, Sparkles, Check, Lock, Mail, Loader2 } from "lucide-react";
import { Footer } from "../components/landing/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TRIAL_URL = "https://pay.rev.cat/kaooarqpwiwxiwlj";
const ease = [0.22, 1, 0.36, 1];
const UNLOCK_KEY = "radiate_topup_unlocked";

const TOPUP = {
  title: "Mini Magnetic Top Up",
  audio: "/magnetic-top-up.mp3",
};

const BENEFITS = [
  "Feel your body settle and your mind go quiet within your first ten-minute session",
  "Reconnect with your radiance and high self-worth — stop settling for less and start showing up in your life in alignment with your vision",
  "Nervous system regulation and visualization work, paired together so you feel safe as you move into the unknown",
];

const fmt = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease, delay: 0.15 + i * 0.1 },
  }),
};

export default function TopUp() {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);

  const [unlocked, setUnlocked] = useState(
    () => localStorage.getItem(UNLOCK_KEY) === "1"
  );
  const [email, setEmail] = useState("");
  const [gate, setGate] = useState("idle"); // idle | loading | error
  const [gateErr, setGateErr] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      a.play().catch(() => {});
      setPlaying(true);
    }
  };

  const restart = () => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = 0;
    setCur(0);
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    a.currentTime = pct * dur;
    setCur(pct * dur);
  };

  const unlock = async (e) => {
    e.preventDefault();
    if (gate === "loading") return;
    if (!email.trim()) {
      setGate("error");
      setGateErr("Please enter your email to unlock.");
      return;
    }
    setGate("loading");
    try {
      await axios.post(`${API}/leads`, {
        email: email.trim(),
        source: "magnetic_topup_page",
      });
      localStorage.setItem(UNLOCK_KEY, "1");
      setUnlocked(true);
      setTimeout(() => {
        const a = audioRef.current;
        if (a) {
          a.play().catch(() => {});
          setPlaying(true);
        }
      }, 150);
    } catch (err) {
      setGate("error");
      setGateErr(
        err?.response?.status === 422
          ? "Please enter a valid email address."
          : "Something went wrong. Please try again."
      );
    }
  };

  const progress = dur ? (cur / dur) * 100 : 0;

  return (
    <div className="grain relative min-h-screen overflow-hidden bg-[#0a0a0c]" data-testid="topup-page">
      {/* glows */}
      <div className="pointer-events-none absolute inset-0 -z-0">
        <div className="orb orb-violet absolute -top-32 left-1/4 h-[34rem] w-[34rem] rounded-full bg-[#7c3aed] blur-[150px] opacity-30" />
        <div className="orb orb-peach absolute top-10 right-0 h-[30rem] w-[30rem] rounded-full bg-[#ffb7b2] blur-[150px] opacity-25" />
        <div className="orb orb-indigo absolute bottom-0 left-0 h-[28rem] w-[28rem] rounded-full bg-[#4f46e5] blur-[160px] opacity-20" />
      </div>

      <div className="relative z-10 mx-auto max-w-2xl px-6 py-24">
        <motion.div variants={fade} initial="hidden" animate="show" className="flex justify-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs uppercase tracking-[0.3em] text-white/70">
            <Sparkles className="h-3.5 w-3.5 text-[#ffb7b2]" /> A special invitation
          </span>
        </motion.div>

        <motion.h1
          variants={fade}
          custom={1}
          initial="hidden"
          animate="show"
          className="mt-8 text-center font-display text-4xl font-thin leading-[1.08] tracking-tight text-white md:text-6xl"
        >
          A mini{" "}
          <span className="italic text-white/85">magnetic top up,</span>
          <br />
          from Radiate.
        </motion.h1>

        <motion.p
          variants={fade}
          custom={2}
          initial="hidden"
          animate="show"
          className="mx-auto mt-6 max-w-md text-center text-base leading-relaxed text-white/60"
          data-testid="topup-founder-note"
        >
          Radiate was founded by Amber Lyon as a tool for her private clients
          that has since opened to the wider community.
        </motion.p>

        {/* gated player card */}
        <motion.div
          variants={fade}
          custom={3}
          initial="hidden"
          animate="show"
          className="relative mt-12 overflow-hidden rounded-[2.5rem] border border-white/10 bg-white/[0.03] p-8 backdrop-blur-2xl md:p-12"
          data-testid="topup-player"
        >
          <div className="relative mx-auto w-full max-w-[280px] overflow-hidden rounded-[1.6rem] border border-white/12 shadow-[0_20px_60px_-20px_rgba(124,58,237,0.55)]">
            <img
              src="/magnetic-top-up-full.webp"
              alt={`${TOPUP.title} cover art`}
              className="block h-auto w-full"
            />
          </div>

          {unlocked ? (
            <div className="mt-8 flex items-center gap-5">
              <button
                onClick={toggle}
                className="grid h-16 w-16 shrink-0 place-items-center rounded-full bg-white text-black transition-transform duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(255,255,255,0.4)]"
                data-testid="topup-play-button"
                aria-label={playing ? "Pause" : "Play"}
              >
                {playing ? (
                  <Pause className="h-6 w-6 fill-current" />
                ) : (
                  <Play className="h-6 w-6 translate-x-[2px] fill-current" />
                )}
              </button>

              <div className="flex-1">
                <div
                  onClick={seek}
                  className="group relative h-2 cursor-pointer rounded-full bg-white/12"
                  data-testid="topup-progress-bar"
                >
                  <div
                    className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-r from-[#ffb7b2] to-[#f9d3b4]"
                    style={{ width: `${progress}%` }}
                  />
                  <div
                    className="absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white opacity-0 shadow transition-opacity group-hover:opacity-100"
                    style={{ left: `${progress}%` }}
                  />
                </div>
                <div className="mt-3 flex items-center justify-between text-xs tabular-nums text-white/50">
                  <span data-testid="topup-current-time">{fmt(cur)}</span>
                  <span data-testid="topup-duration">{fmt(dur)}</span>
                </div>
              </div>

              <button
                onClick={restart}
                className="grid h-12 w-12 shrink-0 place-items-center rounded-full border border-white/15 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
                data-testid="topup-restart-button"
                aria-label="Restart"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <form onSubmit={unlock} className="mt-8" data-testid="topup-unlock-form">
              <div className="flex items-center justify-center gap-2 text-sm text-white/70">
                <Lock className="h-4 w-4 text-[#ffb7b2]" />
                Enter your email to unlock this meditation
              </div>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                <div className="relative flex-1">
                  <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (gate === "error") setGate("idle");
                    }}
                    placeholder="you@email.com"
                    className="w-full rounded-full border border-white/15 bg-white/5 py-3.5 pl-11 pr-4 text-sm text-white placeholder:text-white/35 outline-none transition-colors focus:border-white/40"
                    data-testid="topup-email-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={gate === "loading"}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-medium text-black transition-transform duration-300 hover:scale-105 disabled:opacity-70"
                  data-testid="topup-unlock-button"
                >
                  {gate === "loading" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Unlock & listen"
                  )}
                </button>
              </div>
              {gate === "error" && (
                <p className="mt-3 text-center text-xs text-[#ffb7b2]" data-testid="topup-unlock-error">
                  {gateErr}
                </p>
              )}
            </form>
          )}

          <p className="mt-7 text-center text-sm leading-relaxed text-white/55">
            A short reset to tune you back into your most magnetic state —
            whenever you need it.
          </p>
        </motion.div>

        {/* benefits */}
        <motion.div
          variants={fade}
          custom={4}
          initial="hidden"
          animate="show"
          className="mx-auto mt-14 max-w-xl space-y-3.5"
          data-testid="topup-benefits"
        >
          {BENEFITS.map((b, i) => (
            <div
              key={i}
              className="flex items-start gap-3.5 rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl"
              data-testid={`topup-benefit-${i + 1}`}
            >
              <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-white/5 text-[#ffb7b2]">
                <Check className="h-3.5 w-3.5" />
              </span>
              <p className="text-sm leading-relaxed text-white/70 md:text-base">{b}</p>
            </div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          variants={fade}
          custom={5}
          initial="hidden"
          animate="show"
          className="mt-12 flex flex-col items-center"
        >
          <a
            href={TRIAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2.5 rounded-full bg-white px-9 py-4 text-sm font-medium text-black transition-transform duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(255,255,255,0.35)]"
            data-testid="topup-cta-button"
          >
            Try the full app — one month free
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <p className="mt-5 text-center text-sm text-white/70" data-testid="topup-code-note">
            Use code{" "}
            <span className="rounded-md border border-white/20 bg-white/10 px-2 py-0.5 font-display font-semibold tracking-[0.15em] text-white">
              GRATEFUL
            </span>{" "}
            at checkout.
          </p>
          <p className="mt-3 text-center text-xs text-white/40" data-testid="topup-email-note">
            (please sign up with an email you currently have access to)
          </p>
        </motion.div>
      </div>

      <audio
        ref={audioRef}
        src={TOPUP.audio}
        preload="metadata"
        onLoadedMetadata={(e) => setDur(e.currentTarget.duration)}
        onTimeUpdate={(e) => setCur(e.currentTarget.currentTime)}
        onEnded={() => setPlaying(false)}
      />

      <Footer />
    </div>
  );
}
